import { useUnsavedChangesGuard } from "@/hooks/use-unsaved-changes-guard";
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { VoiceAssistantTextarea } from "@/components/ui/VoiceAssistantTextarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { showInvalidFormFeedback } from "@/lib/invalidFormFeedback";
import TagInput from "@/components/ui/TagInput";
import { invalidateDealViews } from "@/lib/queryKeys";
// Definizione dei tipi per gestire i dati nel componente
interface DealInfo {
  id?: number;
  name: string;
  dealValue: number;
  probability?: number | null;
  myValue?: number | null;
  myValueType?: 'fixed' | 'percentage' | 'annual' | null;
  myPercentage?: number | null;
  myNetValue?: number | null;
  synergyFees?: number | null;
  stageId?: number;
  companyId?: number | null;
  mandanteCompanyId?: number | null;
  mandanteCompany?: { id: number; name: string } | null;
  contactId?: number | null;
  branchId?: number | null;
  expectedCloseDate?: string;
  tags?: string[] | null;
  notes?: string | null;
  source?: string | null;
  status?: string;
  createdAt?: Date | null;
  updatedAt?: Date | null;
}

interface Company {
  id: number;
  name: string;
  relations?: string[];
  [key: string]: any;
}

interface Contact {
  id: number;
  firstName?: string;
  lastName?: string;
  areasOfActivity?: Array<{companyId: number}>;
  [key: string]: any;
}
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { X, Building2, GitBranch, User, ExternalLink } from "lucide-react";
import { useLocation } from "wouter";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Autocomplete, AutocompleteOption } from "@/components/ui/autocomplete";
import { CrmRecordAutocomplete } from "@/components/ui/crm-record-autocomplete";
import { RecordDrawer } from "@/components/ui/RecordDrawer";
import AssociationDecisionDialog from "@/components/associations/AssociationDecisionDialog";
import ContactDrawerContent from "@/components/drawers/ContactDrawerContent";
import DealDrawerContent from "@/components/drawers/DealDrawerContent";
import { apiRequest } from "@/lib/queryClient";
import { isCompanyMandante } from "@shared/companyMandante";

// Partecipante dell'email non ancora un Contatto CRM: 'lead' se esiste gia'
// un Lead auto-creato dal motore email per quell'indirizzo (va convertito
// riusando la conversione manuale globale), 'new' se non esiste nulla (va
// creato un Contatto ex novo). Michele 18/08: quando un deal nasce da
// un'email, TUTTI i partecipanti devono finire collegati al deal, non solo
// quelli gia' risolti a un contatto esistente.
export interface DealPendingContactParticipant {
  source: 'lead' | 'new';
  email: string;
  displayName?: string | null;
  leadId?: number;
}

interface DealModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialData?: DealInfo | Partial<DealInfo> | null;
  onSaved?: (deal: DealInfo & { newContactIds?: number[] }) => void;
  /** Partecipanti dell'origine (es. email) senza identita' CRM: vengono
   * convertiti/creati al salvataggio e allegati come partecipanti del deal. */
  pendingParticipants?: DealPendingContactParticipant[];
}

type DealParticipantType = "contact" | "company" | "branch";
type DealParticipantSelection = { entityType: DealParticipantType; entityId: number; label: string };
type DraftSynergy = DealParticipantSelection & { role: string };

type DealFormData = {
  name: string;
  dealValue: number;
  probability?: number | null;
  myValue?: number | null;
  myValueType?: 'fixed' | 'percentage' | 'annual' | null;
  myPercentage?: number | null;
  stageId: number;
  companyId?: number | null;
  mandanteCompanyId?: number | null;
  contactId?: number | null;
  branchId?: number | null;
  expectedCloseDate?: string;
  tags?: string[] | null;
  notes?: string | null;
};

export default function DealModal({ open, onOpenChange, initialData, onSaved, pendingParticipants }: DealModalProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [, navigate] = useLocation();
  const [tagsInput, setTagsInput] = useState("");
  const formInitializedRef = useRef(false);
  
  // Form reference for alert dialog submission
  const formRef = useRef<HTMLFormElement>(null);
  const primaryContactFieldRef = useRef<HTMLDivElement>(null);

  const [selectedContactId, setSelectedContactId] = useState<number | null>(null);
  const [participantType, setParticipantType] = useState<DealParticipantType>("contact");
  const [participantSelections, setParticipantSelections] = useState<DealParticipantSelection[]>([]);
  const [participantDecision, setParticipantDecision] = useState<{ id: number; label: string; entityType: DealParticipantType; entityId: number } | null>(null);
  const [synergyType, setSynergyType] = useState<DealParticipantType>('contact');
  const [draftSynergies, setDraftSynergies] = useState<DraftSynergy[]>([]);
  const [showNoCompanyAlert, setShowNoCompanyAlert] = useState(false);
  // Regola Michele 20 lug 2026: un deal puo' essere legato alla sola persona
  // ("contatto indipendente"): azienda non obbligatoria, ricerca libera del
  // contatto, nessun alert "nessuna azienda" al salvataggio.
  const [independentContact, setIndependentContact] = useState(false);
  const [missingPrimaryContact, setMissingPrimaryContact] = useState(false);
  // Hide the Sheet immediately after a valid submit while keeping the draft
  // mounted. A failed primary write can then reveal the same form unchanged.
  const [optimisticallyHidden, setOptimisticallyHidden] = useState(false);
  const isEditMode = !!initialData && initialData.id !== undefined;
  // Coda di verifica post-salvataggio: contatti appena creati/convertiti da
  // pendingParticipants, poi il deal stesso. Chiusa l'ultima tappa si torna
  // dove si era (questi drawer sono overlay sopra la pagina di origine, non
  // navigazione: non serve nessun meccanismo di "ritorno" dedicato).
  const [reviewContactQueue, setReviewContactQueue] = useState<number[]>([]);
  const [reviewDealId, setReviewDealId] = useState<number | null>(null);

  // PERFORMANCE OPTIMIZED: Use dedicated lightweight endpoint for deal form data
  const { data: formData, isLoading: isLoadingFormData } = useQuery<any>({
    queryKey: ['/api/deals/form-data'],
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });

  // Extract data from optimized response
  const contacts = formData?.contacts || [];
  const companies = formData?.companies || [];
  const stages = formData?.stages || [];
  
  // Legacy compatibility flags
  const isLoadingContacts = isLoadingFormData;
  const isLoadingCompanies = isLoadingFormData;
  const isLoadingStages = isLoadingFormData;

  const dealSchema = z.object({
    name: z.string().trim().min(1, t("deals.validation.nameRequired", "Il nome dell'opportunità è obbligatorio")),
    dealValue: z.coerce.number().min(0, t("deals.validation.valuePositive", "Il valore deve essere un numero positivo")),
    probability: z.coerce.number().int().min(0).max(100).optional().nullable(),
    myValue: z.coerce.number().optional().nullable(),
    myValueType: z.enum(['fixed', 'percentage', 'annual']).optional().nullable(),
    myPercentage: z.coerce.number().optional().nullable(),
    stageId: z.coerce.number().min(1, t("deals.validation.stageRequired", "Fase pipeline richiesta")),
    companyId: z.coerce.number().optional().nullable(),
    mandanteCompanyId: z.coerce.number().optional().nullable(),
    contactId: z.coerce.number().optional().nullable(),
    branchId: z.coerce.number().optional().nullable(),
    expectedCloseDate: z.string().optional(),
    tags: z.array(z.string()).optional().nullable(),
    notes: z.string().optional().nullable()
  });

  const { register, handleSubmit, reset, setValue, getValues, watch, control, formState: { errors, isDirty } } = useForm<DealFormData>({
    resolver: zodResolver(dealSchema),
    defaultValues: {
      name: "",
      dealValue: undefined as any,
      probability: null,
      myValue: null,
      myValueType: null,
      myPercentage: null,
      stageId: 1,
      companyId: undefined,
      mandanteCompanyId: undefined,
      contactId: undefined,
      branchId: undefined,
      expectedCloseDate: "",
      tags: [],
      notes: ""
    }
  });

  const { requestClose, closeAfterSave } = useUnsavedChangesGuard(open && !optimisticallyHidden, isDirty, onOpenChange);

  // Sync selectedContactId with form
  useEffect(() => {
    if (selectedContactId !== null) {
      setValue("contactId", selectedContactId);
    }
  }, [selectedContactId, setValue]);

  const watchedCompanyId = watch("companyId") as number | null | undefined;
  const watchedMandanteCompanyId = watch("mandanteCompanyId") as number | null | undefined;
  const requiresPrimaryContact = Boolean(watchedCompanyId || independentContact);
  useEffect(() => {
    if (!open) {
      setMissingPrimaryContact(false);
      setOptimisticallyHidden(false);
    }
  }, [open]);
  const { data: dealContactGroups } = useQuery<any>({
    queryKey: [`/api/companies/${watchedCompanyId}/contact-groups`],
    enabled: open && !!watchedCompanyId,
  });
  const { data: existingDealParticipants } = useQuery<Array<{ id: number; entityType: DealParticipantType; entityId: number; entityName?: string | null }>>({
    queryKey: [`/api/deals/${initialData?.id}/participants`],
    queryFn: () => apiRequest(`/api/deals/${initialData?.id}/participants`),
    enabled: open && isEditMode && Boolean(initialData?.id),
  });

  const relatedContactSuggestions = useMemo(() => {
    const groups = Array.isArray(dealContactGroups?.groups) ? dealContactGroups.groups : [];
    const byId = new Map<number, { id: number; label: string; context: string }>();
    groups.forEach((group: any) => {
      (Array.isArray(group.contacts) ? group.contacts : []).forEach((contact: any) => {
        const id = Number(contact.id);
        if (!Number.isSafeInteger(id) || id <= 0 || id === selectedContactId) return;
        byId.set(id, {
          id,
          label: [contact.firstName, contact.lastName].filter(Boolean).join(' ') || `Contatto ${id}`,
          context: group.companyName || 'Azienda collegata',
        });
      });
    });
    return Array.from(byId.values());
  }, [dealContactGroups, selectedContactId]);

  const excludedOrdinaryParticipantIds = useMemo(() => Array.from(new Set([
    ...(participantType === 'contact' && selectedContactId ? [selectedContactId] : []),
    ...(participantType === 'company' && watchedCompanyId ? [Number(watchedCompanyId)] : []),
    ...draftSynergies.filter((item) => item.entityType === participantType).map((item) => item.entityId),
  ].filter((id) => Number.isSafeInteger(id) && Number(id) > 0))), [draftSynergies, participantType, selectedContactId, watchedCompanyId]);

  const excludedSynergyIds = useMemo(() => Array.from(new Set([
    ...(synergyType === 'contact' && selectedContactId ? [selectedContactId] : []),
    ...(synergyType === 'company' && watchedCompanyId ? [Number(watchedCompanyId)] : []),
    ...participantSelections.filter((item) => item.entityType === synergyType).map((item) => item.entityId),
  ].filter((id) => Number.isSafeInteger(id) && Number(id) > 0))), [participantSelections, selectedContactId, synergyType, watchedCompanyId]);

  useEffect(() => {
    if (!open || !isEditMode || !Array.isArray(existingDealParticipants)) return;
    setParticipantSelections(existingDealParticipants
      .filter((item) => ["contact", "company", "branch"].includes(item.entityType))
      .map((item) => ({
        entityType: item.entityType,
        entityId: Number(item.entityId),
        label: item.entityName || `${item.entityType} ${item.entityId}`,
      })));
  }, [existingDealParticipants, isEditMode, open]);


  // Helper function che utilizza direttamente i valori del form
  const getSelectedCompanyId = () => {
    const value = getValues("companyId");
    return typeof value === 'number' && !isNaN(value) ? value : null;
  };

  const getCompanyIdForContact = (contactId: number | null | undefined) => {
    if (!contactId || !Array.isArray(contacts)) return null;
    const contact = contacts.find((item: Contact) => item.id === contactId);
    const directCompanyId = Number(contact?.companyId);
    if (Number.isSafeInteger(directCompanyId) && directCompanyId > 0) return directCompanyId;
    const primaryArea = Array.isArray(contact?.areasOfActivity)
      ? contact.areasOfActivity.find((area: any) => area?.isPrimary && area?.companyId) || contact.areasOfActivity.find((area: any) => area?.companyId)
      : null;
    const areaCompanyId = Number(primaryArea?.companyId);
    return Number.isSafeInteger(areaCompanyId) && areaCompanyId > 0 ? areaCompanyId : null;
  };
  
  // Funzione per aggiornare l'azienda nel form
  const setCompanyIdInForm = (id: number | null) => {
    setValue("companyId", id);
    setSelectedContactId(null);
    if (id && Array.isArray(contacts)) {
      const linkedContacts = contacts.filter((contact: any) => contact.companyId === id);
      if (linkedContacts.length === 1) {
        setValue("contactId", linkedContacts[0].id);
        setSelectedContactId(linkedContacts[0].id);
      } else {
        setValue("contactId", null);
      }
    } else {
      setValue("contactId", null);
    }
  };
  
  // Initialize form when in edit mode or reset for create mode
  useEffect(() => {
    if (!open) return;
    if (initialData && isLoadingFormData) return;
    
    if (initialData && !formInitializedRef.current) {
      // Set form values from the existing deal
      if (initialData.name) setValue("name", String(initialData.name));
      if (initialData.dealValue !== undefined) setValue("dealValue", Number(initialData.dealValue));
      if (initialData.probability !== undefined) setValue("probability", initialData.probability === null ? null : Number(initialData.probability));
      if (initialData.myValue !== undefined) setValue("myValue", initialData.myValue ? Number(initialData.myValue) : null);
      if (initialData.myValueType !== undefined) setValue("myValueType", initialData.myValueType);
      if (initialData.myPercentage !== undefined) setValue("myPercentage", initialData.myPercentage ? Number(initialData.myPercentage) : null);
      if (initialData.stageId) setValue("stageId", Number(initialData.stageId));
      
      const initialContactId = initialData.contactId !== undefined && initialData.contactId !== null
        ? Number(initialData.contactId)
        : null;
      // Deal salvato senza azienda (edit): e' un deal da contatto indipendente
      // (regola Michele 20 lug) — non inferire l'azienda dal contatto a ogni
      // riapertura, altrimenti il deal non resterebbe mai "solo persona".
      const explicitlyIndependent = isEditMode && initialData.companyId === null;
      setIndependentContact(explicitlyIndependent);
      const initialCompanyId = explicitlyIndependent
        ? null
        : initialData.companyId !== undefined && initialData.companyId !== null
          ? Number(initialData.companyId)
          : getCompanyIdForContact(initialContactId);

      if (initialCompanyId) {
        setValue("companyId", initialCompanyId);
      } else if (initialData.companyId === null) {
        setValue("companyId", null);
      }
      if (initialData.mandanteCompanyId !== undefined) {
        setValue(
          "mandanteCompanyId",
          initialData.mandanteCompanyId !== null ? Number(initialData.mandanteCompanyId) : null,
        );
      }

      if (initialContactId) {
        setValue("contactId", initialContactId);
        setSelectedContactId(initialContactId);
      } else if (initialData.contactId === null) {
        setValue("contactId", null);
        setSelectedContactId(null);
      } else if (initialCompanyId) {
        setCompanyIdInForm(initialCompanyId);
      }

      if (initialData.branchId !== undefined) {
        setValue("branchId", initialData.branchId !== null ? Number(initialData.branchId) : null);
      }

      // Format date
      if (initialData.expectedCloseDate) {
        const date = new Date(initialData.expectedCloseDate);
        if (!isNaN(date.getTime())) {
          setValue("expectedCloseDate", date.toISOString().split('T')[0]);
        }
      }

      // Set tags
      if (initialData.tags && Array.isArray(initialData.tags)) {
        setTagsInput(initialData.tags.join(", "));
      }

      // Set notes
      if (initialData.notes !== undefined) {
        setValue("notes", initialData.notes || "");
      }
      
      formInitializedRef.current = true;
    } else if (!initialData && !formInitializedRef.current) {
      // Reset for new deal creation
      reset();
      setTagsInput("");
      setParticipantType("contact");
      setParticipantSelections([]);
      setSynergyType('contact');
      setDraftSynergies([]);
      setCompanyIdInForm(null);
      
      // Set default stage if available
      if (Array.isArray(stages) && stages.length > 0 && stages[0]?.id) {
        setValue("stageId", stages[0].id);
      }
      
      formInitializedRef.current = true;
    }
  }, [contacts, initialData, isLoadingFormData, open, reset, setValue, stages]);

  // When modal closes, reset the initialization flag
  useEffect(() => {
    if (!open) {
      formInitializedRef.current = false;
      setParticipantSelections([]);
      setParticipantType("contact");
      setDraftSynergies([]);
      setSynergyType('contact');
    }
  }, [open]);

  // Autocomplete options for companies
  const companyOptions: AutocompleteOption[] = useMemo(() => {
    if (!companies || !Array.isArray(companies)) return [];
    return companies.map((company: any) => ({
      value: company.id.toString(),
      label: company.name,
      icon: <Building2 className="h-4 w-4" />,
    }));
  }, [companies]);

  const mandanteCompanyOptions: AutocompleteOption[] = useMemo(() => (
    (Array.isArray(companies) ? companies : [])
      .filter((company: Company) => isCompanyMandante(company.relations))
      .map((company: Company) => ({
        value: String(company.id),
        label: company.name,
        icon: <Building2 className="h-4 w-4 text-amber-700" />,
      }))
  ), [companies]);

  const stageOptions: AutocompleteOption[] = useMemo(() => {
    const unique = new Map<string, any>();
    (Array.isArray(stages) ? stages : []).forEach((stage: any) => {
      if (stage?.id && stage?.name && !unique.has(stage.name)) unique.set(stage.name, stage);
    });
    return Array.from(unique.values())
      .sort((a, b) => Number(a.order || 0) - Number(b.order || 0))
      .map((stage) => ({ value: String(stage.id), label: stage.name }));
  }, [stages]);

  // Autocomplete options for contacts: linked company contacts first, then others
  const contactOptions: AutocompleteOption[] = useMemo(() => {
    if (!contacts || !Array.isArray(contacts)) return [];

    const selectedCompanyId = getSelectedCompanyId();
    if (!selectedCompanyId) {
      return contacts.map((contact: any) => ({
        value: contact.id.toString(),
        label: `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || t("deals.form.unnamed_contact", "Contatto senza nome"),
        icon: <User className="h-4 w-4" />,
      }));
    }

    const directGroup = dealContactGroups?.groups?.find((group: any) => group.relationship === 'direct');
    const linkedContacts = Array.isArray(directGroup?.contacts) ? directGroup.contacts : [];
    const linkedIds = new Set(linkedContacts.map((contact: any) => Number(contact.id)));
    const controllingGroup = dealContactGroups?.groups?.find((group: any) => group.relationship === 'controlling_company');
    const controllingContacts = Array.isArray(controllingGroup?.contacts) ? controllingGroup.contacts : [];
    const controllingIds = new Set(controllingContacts.map((contact: any) => Number(contact.id)));
    const otherContacts = contacts.filter((contact: any) => !linkedIds.has(Number(contact.id)) && !controllingIds.has(Number(contact.id)));

    const options: AutocompleteOption[] = [];
    if (linkedContacts.length > 0) {
      options.push({
        value: '_header_linked',
        label: '',
        group: directGroup?.companyName || t("deals.form.company_contacts", "Societa del Deal"),
        groupHeader: true,
      });
      options.push(...linkedContacts.map((contact: any) => ({
        value: contact.id.toString(),
        label: `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || t("deals.form.unnamed_contact", "Contatto senza nome"),
        icon: <User className="h-4 w-4" />,
        highlighted: true,
      })));
    }
    if (controllingContacts.length > 0) {
      options.push({ value: '_header_controlling', label: '', group: controllingGroup.companyName, groupHeader: true });
      options.push(...controllingContacts.map((contact: any) => ({
        value: contact.id.toString(),
        label: `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || t("deals.form.unnamed_contact", "Contatto senza nome"),
        icon: <User className="h-4 w-4" />,
        highlighted: true,
      })));
    }
    if (otherContacts.length > 0) {
      options.push({
        value: '_header_others',
        label: '',
        group: t("deals.form.other_contacts", "Altri contatti"),
        groupHeader: true,
      });
      options.push(...otherContacts.map((contact: any) => ({
        value: contact.id.toString(),
        label: `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || t("deals.form.unnamed_contact", "Contatto senza nome"),
        icon: <User className="h-4 w-4" />,
      })));
    }
    return options;
  }, [contacts, dealContactGroups, watchedCompanyId, t]);

  // Save deal mutation
  const saveDeal = useMutation({
    mutationFn: async (data: DealFormData) => {
      // Prepare deal data - ONLY send fields expected by backend schema
      const dealData: any = {
        name: data.name?.trim() || "", // Ensure name is never empty
        dealValue: data.dealValue ? data.dealValue.toString() : "0", // Always provide a dealValue
        myValue: data.myValue !== undefined && data.myValue !== null ? data.myValue.toString() : null,
        myValueType: data.myValueType || null,
        myPercentage: data.myPercentage !== undefined && data.myPercentage !== null ? data.myPercentage.toString() : null,
        stageId: data.stageId || 1, // Il server deriva lo stato dall'esito della fase.
      };

      // An empty field must not override the database's default probability
      // when creating a deal (or clear an existing value during an edit).
      if (data.probability !== null && data.probability !== undefined) {
        dealData.probability = data.probability;
      }

      // `null` is an explicit contact-only decision; preserving it lets the
      // common writer distinguish it from an accidentally omitted company.
      dealData.companyId = data.companyId ?? null;
      dealData.mandanteCompanyId = data.mandanteCompanyId ?? null;
      dealData.contactId = data.contactId ?? null;
      if (data.branchId !== undefined) {
        dealData.branchId = data.branchId ?? null;
      } else if (initialData?.branchId !== undefined) {
        dealData.branchId = initialData.branchId ?? null;
      }
      
      // Debug: log the contactId value
      console.log("[DealModal] contactId from form:", data.contactId, "type:", typeof data.contactId);

      if (data.notes?.trim()) {
        dealData.notes = data.notes.trim();
      }

      // Process tags - always provide as array
      if (tagsInput.trim()) {
        dealData.tags = tagsInput.split(",").map(tag => tag.trim()).filter(tag => tag.length > 0);
      } else {
        dealData.tags = [];
      }

      // Format date properly
      if (data.expectedCloseDate) {
        const dateObj = new Date(data.expectedCloseDate);
        if (!isNaN(dateObj.getTime())) {
          dealData.expectedCloseDate = dateObj.toISOString();
        }
      }

      // Origine del Deal (es. 'meeting' dal bottone Crea Deal del meeting):
      // pass-through della colonna deals.source, solo in creazione e solo se
      // il chiamante la dichiara — nessuna origine inventata dal form.
      if (!isEditMode && initialData?.source) {
        dealData.source = initialData.source;
      }

      // Debug: Log the exact payload being sent

      // Determine method and URL
      const method = isEditMode ? "PATCH" : "POST";
      const url = isEditMode ? `/api/deals/${initialData?.id}` : "/api/deals";

      // The canonical client also emits the committed-write event, so all
      // active CRM views refresh after a Deal changes.
      const savedDeal = await apiRequest({
        url,
        method,
        data: dealData,
        notifySuccess: false,
      });

      const savedDealId = Number(savedDeal?.id ?? initialData?.id);
      const failedParticipants: string[] = [];
      if (Number.isSafeInteger(savedDealId) && savedDealId > 0) {
        const persistedParticipants = existingDealParticipants ?? [];
        const primaryKeys = new Set([
          data.contactId ? `contact:${data.contactId}` : null,
          data.companyId ? `company:${data.companyId}` : null,
          data.branchId ? `branch:${data.branchId}` : null,
        ].filter(Boolean));
        const selected = participantSelections.filter((item) => !primaryKeys.has(`${item.entityType}:${item.entityId}`));
        const currentKeys = new Set(persistedParticipants.map((item) => `${item.entityType}:${item.entityId}`));

        for (const participant of selected.filter((item) => !currentKeys.has(`${item.entityType}:${item.entityId}`))) {
          try {
            await apiRequest('POST', `/api/deals/${savedDealId}/participants`, {
              entityType: participant.entityType,
              entityId: participant.entityId,
            });
          } catch (error) {
            console.error('[DealModal] Partecipante non salvato', error);
            failedParticipants.push(participant.label);
          }
        }
      }

      // Partecipanti dell'origine (email) senza identita' CRM: converte il
      // Lead esistente o crea un nuovo Contatto, riusando le rotte globali
      // /api/leads/:id/convert-contact e /api/contacts — mai una scrittura
      // dedicata. Ogni nuovo contatto viene poi allegato al deal come
      // partecipante ordinario, cosi' come gli altri.
      const newContactIds: number[] = [];
      if (Number.isSafeInteger(savedDealId) && savedDealId > 0 && pendingParticipants && pendingParticipants.length > 0) {
        for (const pending of pendingParticipants) {
          try {
            let contactId: number | null = null;
            if (pending.source === 'lead' && pending.leadId) {
              const result = await apiRequest('POST', `/api/leads/${pending.leadId}/convert-contact`, {
                confirmation: 'manual_lead_conversion',
              });
              contactId = Number(result?.contact?.id);
            } else {
              const rawName = (pending.displayName || pending.email.split('@')[0] || '').trim();
              const [firstName, ...rest] = rawName.split(/\s+/).filter(Boolean);
              const created = await apiRequest('POST', '/api/contacts', {
                firstName: firstName || 'Contatto',
                lastName: rest.join(' ') || 'Da Email',
                companyEmail: pending.email,
              });
              contactId = Number(created?.id);
            }
            if (Number.isSafeInteger(contactId) && contactId > 0 && contactId !== data.contactId) {
              newContactIds.push(contactId);
              await apiRequest('POST', `/api/deals/${savedDealId}/participants`, {
                entityType: 'contact',
                entityId: contactId,
              });
            }
          } catch (error) {
            console.error('[DealModal] Impossibile creare/convertire contatto da partecipante pending', pending, error);
            failedParticipants.push(pending.displayName || pending.email);
          }
        }
      }

      // A functional Sinergia is not an ordinary Deal participant. Persist it
      // through the canonical Synergies API only after the manual Deal exists.
      const failedSynergies: string[] = [];
      if (!isEditMode) {
        const primarySynergyKeys = new Set([
          data.contactId ? `contact:${data.contactId}` : null,
          data.companyId ? `company:${data.companyId}` : null,
          data.branchId ? `branch:${data.branchId}` : null,
        ].filter(Boolean));
        for (const synergy of draftSynergies) {
          if (primarySynergyKeys.has(`${synergy.entityType}:${synergy.entityId}`)) {
            failedSynergies.push(`${synergy.label} (già principale)`);
            continue;
          }
          try {
            await apiRequest('POST', '/api/synergies', {
              dealId: savedDealId,
              entityType: synergy.entityType,
              entityId: synergy.entityId,
              roleTags: synergy.role.trim() ? [synergy.role.trim()] : [],
            });
          } catch (error) {
            console.error('[DealModal] Sinergia non salvata', error);
            failedSynergies.push(synergy.label);
          }
        }
      }

      return { ...savedDeal, newContactIds, failedSynergies, failedParticipants };
    },
    onSuccess: (savedDeal) => {
      const incompleteLinks = [
        savedDeal.failedParticipants?.length ? `Partecipanti: ${savedDeal.failedParticipants.join(', ')}` : null,
        savedDeal.failedSynergies?.length ? `Sinergie: ${savedDeal.failedSynergies.join(', ')}` : null,
      ].filter(Boolean);
      toast(incompleteLinks.length ? {
        title: 'Opportunità salvata con collegamenti da completare',
        description: `${incompleteLinks.join(' · ')}. Apri la scheda dell'opportunità per riprovare.`,
      } : {
        title: t("common.success", "Operazione completata"),
        description: isEditMode ? t("deals.success.updated", "Opportunità aggiornata con successo") : t("deals.success.created", "Opportunità creata con successo"),
      });

      onSaved?.(savedDeal);

      // Mantieni subito coerenti sia la scheda record sia le viste lista.
      // La precedente clear() globale rimuoveva anche la query attiva del
      // dettaglio e poteva lasciare il modal alimentato dal vecchio deal.
      if (savedDeal?.id) {
        queryClient.setQueryData([`/api/deals/${savedDeal.id}`], savedDeal);
      }
      invalidateDealViews(queryClient, savedDeal?.id, ['participants']);
      queryClient.invalidateQueries({ predicate: (query) => String(query.queryKey[0] ?? '').startsWith('/api/synergies') });
      if (Array.isArray(savedDeal?.newContactIds) && savedDeal.newContactIds.length > 0) {
        queryClient.invalidateQueries({ queryKey: ['/api/contacts'] });
        queryClient.invalidateQueries({ queryKey: ['/api/leads'] });
      }

      // Close the create/edit form. Se il salvataggio ha creato nuovi
      // contatti (da partecipanti pending), non si torna subito all'origine:
      // si apre prima un drawer di verifica per ciascun nuovo contatto, poi
      // il drawer del deal — solo alla chiusura di quest'ultimo si torna
      // dove si era (vedi reviewContactQueue/reviewDealId sopra).
      closeAfterSave();
      const createdIds = Array.isArray(savedDeal?.newContactIds)
        ? savedDeal.newContactIds.filter((id: unknown): id is number => Number.isSafeInteger(id) && Number(id) > 0)
        : [];
      if (createdIds.length > 0 && savedDeal?.id) {
        setReviewContactQueue(createdIds);
        setReviewDealId(Number(savedDeal.id));
      }
      reset();
      setTagsInput("");
      setDraftSynergies([]);
      setCompanyIdInForm(null);
      setShowNoCompanyAlert(false);
      setMissingPrimaryContact(false);
      formInitializedRef.current = false;

    },
    onError: (error) => {
      setOptimisticallyHidden(false);
      toast({
        title: isEditMode ? "Opportunità non aggiornata" : "Opportunità non creata",
        description: "Il salvataggio non è riuscito. Il modulo è stato riaperto con i dati inseriti: verifica e riprova.",
        variant: "destructive",
      });
    }
  });

  const startSave = (data: DealFormData) => {
    setOptimisticallyHidden(true);
    toast({ title: 'Salvataggio in corso', description: "L'opportunità viene salvata. Riceverai conferma o un avviso se qualcosa non riesce." });
    saveDeal.mutate(data);
  };

  const onSubmit = (data: DealFormData) => {
    // Ensure contactId is read from the form state
    const formValues = getValues();
    const submitData = {
      ...data,
      contactId: selectedContactId ?? formValues.contactId ?? null,
      companyId: formValues.companyId,
    };
    if ((submitData.companyId || independentContact) && !submitData.contactId) {
      setMissingPrimaryContact(true);
      primaryContactFieldRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setMissingPrimaryContact(false);
    if (!submitData.companyId && !independentContact) {
      setShowNoCompanyAlert(true);
      return;
    }
    console.log("[DealModal] Submitting deal with contactId:", submitData.contactId);
    startSave(submitData);
  };

  return (
    <>
      {/* Alert Dialog for creating deal without company */}
      <AlertDialog open={showNoCompanyAlert} onOpenChange={setShowNoCompanyAlert}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("deals.alerts.noCompany", "Nessuna azienda selezionata")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("deals.alerts.noCompanyDescription", "Per creare un'opportunità è necessario selezionare un'azienda. Vuoi procedere comunque?")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel", "Annulla")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (formRef.current) {
                  const formData = getValues();
                  setShowNoCompanyAlert(false);
                  startSave({ ...formData, contactId: selectedContactId ?? formData.contactId ?? null });
                }
              }}
            >
              {t("common.proceed", "Procedi")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Sheet open={open && !optimisticallyHidden} onOpenChange={requestClose}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-2xl">
          <SheetHeader>
            <div className="flex items-start justify-between gap-3">
              <SheetTitle className="text-xl font-semibold text-foreground notion-title">{isEditMode ? t("deals.edit_deal", "Modifica opportunità") : t("deals.new_deal", "Nuova opportunità")}</SheetTitle>
              {/* Dal Kanban questo pannello era un vicolo cieco: si poteva
                  modificare il deal ma non c'era alcun modo di raggiungere la
                  sua scheda completa (timeline, email, task, note AI...).
                  Il link vale ovunque questo pannello si apra in modifica —
                  Kanban, tabella, tag deal sulle email — non solo dal Kanban. */}
              {isEditMode && initialData?.id && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 shrink-0 gap-1.5 rounded-none text-xs"
                  onClick={() => {
                    if (requestClose(false)) navigate(`/deals/${initialData.id}`);
                  }}
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  Apri scheda completa
                </Button>
              )}
            </div>
            <SheetDescription>
              Configura dati principali, relazioni e partecipanti dell'opportunità.
            </SheetDescription>
          </SheetHeader>
          
          <form ref={formRef} onSubmit={handleSubmit(onSubmit, showInvalidFormFeedback)} className="space-y-4 py-4">
            <p className="text-xs text-muted-foreground"><span className="font-semibold text-amber-700">* Campo obbligatorio</span> · Il contatto principale è richiesto con un'azienda o per un Deal legato alla sola persona.</p>
            {/* Basic Deal Info */}
            <div className="space-y-4">
              {/* Row 1: Name + Deal Value */}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="name" className="w-fit border-b-2 border-amber-500 pb-0.5 text-sm font-medium text-gray-700">{t("deals.form.name", "Nome opportunità")} <span className="text-amber-700" aria-hidden="true">*</span></Label>
                  <Input 
                    id="name" 
                    placeholder={t("deals.form.name_placeholder", "Inserisci nome opportunità")}
                    className="notion-input"
                    {...register("name")}
                  />
                  {errors.name && (
                    <p className="text-xs text-red-500">{errors.name.message}</p>
                  )}
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="dealValue">{t("deals.form.value", "Valore totale")}</Label>
                  <Input 
                    id="dealValue" 
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    placeholder="0"
                    {...register("dealValue")}
                    onFocus={(e) => { e.target.select(); }}
                    onClick={(e) => { (e.target as HTMLInputElement).select(); }}
                  />
                  {errors.dealValue && (
                    <p className="text-xs text-red-500">{errors.dealValue.message}</p>
                  )}
                </div>
              </div>

              {/* Row 2: My Value + My Value Type */}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="myValue">{t("deals.form.my_value", "Mio valore")}</Label>
                  <Input 
                    id="myValue" 
                    type="number"
                    min="0"
                    placeholder="0"
                    {...register("myValue")}
                  />
                  {errors.myValue && (
                    <p className="text-xs text-red-500">{errors.myValue.message}</p>
                  )}
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="myValueType">{t("deals.form.my_value_type", "Tipo mio valore")}</Label>
                  <Controller
                    name="myValueType"
                    control={control}
                    render={({ field }) => (
                      <Select
                        value={field.value || ""}
                        onValueChange={(value) => {
                          field.onChange(value || null);
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={t("deals.form.select_type", "Seleziona tipo")} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="fixed">Fisso</SelectItem>
                          <SelectItem value="percentage">Percentuale</SelectItem>
                          <SelectItem value="annual">Annuale</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  />
                </div>
              </div>

              {/* Row 3: My Percentage + Stage */}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="myPercentage">{t("deals.form.my_percentage", "Mia percentuale")}</Label>
                  <Input 
                    id="myPercentage" 
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    placeholder="0"
                    {...register("myPercentage")}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="stageId" className="w-fit border-b-2 border-amber-500 pb-0.5">{t("deals.form.pipeline_stage", "Fase pipeline")} <span className="text-amber-700" aria-hidden="true">*</span></Label>
                  <Controller
                    name="stageId"
                    control={control}
                    render={({ field }) => (
                      <Autocomplete
                        options={stageOptions}
                        value={field.value?.toString() || ""}
                        onValueChange={(value) => field.onChange(value ? Number(value) : 0)}
                        placeholder={t("deals.form.select_stage", "Seleziona fase")}
                        searchPlaceholder="Cerca fase..."
                      />
                    )}
                  />
                  {errors.stageId && (
                    <p className="text-xs text-red-500">{errors.stageId.message}</p>
                  )}
                </div>
              </div>
              
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="min-w-0 space-y-2">
                  <Label htmlFor="expectedCloseDate">{t("deals.form.expected_close_date", "Data di chiusura prevista")}</Label>
                  <Input 
                    id="expectedCloseDate" 
                    type="date"
                    {...register("expectedCloseDate")}
                  />
                </div>
                <div className="min-w-0 space-y-2">
                  <Label htmlFor="probability">Probabilità di chiusura (%)</Label>
                  <Input id="probability" type="number" min="0" max="100" step="1" placeholder="0–100" {...register("probability", { setValueAs: (value) => value === '' ? null : Number(value) })} />
                  {errors.probability && <p className="text-xs text-destructive">Inserisci un valore da 0 a 100.</p>}
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="tags">{t("deals.form.tags", "Etichette")}</Label>
                  <TagInput
                    value={tagsInput.split(",").map(t => t.trim()).filter(t => t.length > 0)}
                    onChange={(tags) => setTagsInput(tags.join(", "))}
                    placeholder={t("deals.form.tags_placeholder", "Inserisci etichette separate da virgole")}
                  />
                </div>
              </div>
            </div>
            
            {/* Company and Contact Selection */}
            <div className="space-y-4 border-t pt-4">
              <div className="space-y-2">
                <Label htmlFor="companyId">{t("deals.form.company", "Azienda")}</Label>
                <Controller
                  name="companyId"
                  control={control}
                  render={({ field }) => (
                    <CrmRecordAutocomplete
                      entityType="company"
                      initialOptions={companyOptions}
                      value={field.value?.toString() || ""}
                      onValueChange={(value) => {
                        const newCompanyId = value ? parseInt(value, 10) : null;
                        field.onChange(newCompanyId);
                        setCompanyIdInForm(newCompanyId);
                        setMissingPrimaryContact(false);
                        if (newCompanyId) setIndependentContact(false);
                      }}
                      placeholder={t("deals.form.select_company", "Seleziona azienda")}
                      searchPlaceholder={t("deals.form.search_company", "Cerca azienda...")}
                      selectedLabel={companyOptions.find((option) => option.value === field.value?.toString())?.label}
                      disabled={isLoadingCompanies || independentContact}
                    />
                  )}
                />
                {errors.companyId && (
                  <p className="text-xs text-red-500">{errors.companyId.message}</p>
                )}
              </div>

              <div className="space-y-2 rounded-md border border-amber-300 bg-amber-50 p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <Label htmlFor="mandanteCompanyId" className="font-semibold text-amber-950">Mandante</Label>
                  <Badge className="border-0 bg-amber-500 text-amber-950 hover:bg-amber-500">Relazione operativa</Badge>
                </div>
                <Controller
                  name="mandanteCompanyId"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      options={mandanteCompanyOptions}
                      value={field.value?.toString() || ""}
                      onValueChange={(value) => field.onChange(value ? Number(value) : null)}
                      placeholder="Nessuna — attività e fatturato EXPERVISER"
                      searchPlaceholder="Cerca azienda mandante..."
                    />
                  )}
                />
                <p className="text-xs leading-5 text-amber-900">
                  Selezionala solo quando il Deal è svolto per conto di quella azienda. L’azienda principale indica invece il cliente o il soggetto del Deal.
                </p>
                {watchedMandanteCompanyId && mandanteCompanyOptions.every((option) => Number(option.value) !== Number(watchedMandanteCompanyId)) ? (
                  <p className="text-xs font-medium text-destructive">La mandante salvata non è più classificata come Mandante: correggi la relazione nella scheda Azienda.</p>
                ) : null}
              </div>

              {/* Regola Michele 20 lug 2026: deal legato alla sola persona —
                  azienda non obbligatoria, contatto a ricerca libera */}
              <label className="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground">
                <Checkbox
                  checked={independentContact}
                  onCheckedChange={(checked) => {
                    const on = checked === true;
                    setIndependentContact(on);
                    if (on) {
                      // Azienda svincolata; il contatto NON viene toccato.
                      setValue("companyId", null);
                      setMissingPrimaryContact(false);
                    }
                  }}
                />
                <span>{t("deals.form.independent_contact", "Contatto indipendente — deal legato alla sola persona (nessuna azienda)")}</span>
              </label>
              
              <div ref={primaryContactFieldRef} className={`space-y-2 rounded-md p-3 ${missingPrimaryContact ? "border border-destructive bg-destructive/5" : requiresPrimaryContact ? "border border-amber-300 bg-amber-50/40" : ""}`}>
                <Label htmlFor="contactId" className={requiresPrimaryContact ? "w-fit border-b-2 border-amber-500 pb-0.5" : ""}>{t("deals.form.primary_contact", "Contatto principale")}{requiresPrimaryContact && <span className="ml-1 text-amber-700" aria-hidden="true">*</span>}</Label>
                <CrmRecordAutocomplete
                  entityType="contact"
                  initialOptions={contactOptions}
                  companyScopeId={getSelectedCompanyId() || null}
                  value={selectedContactId?.toString() || ""}
                  onValueChange={(value) => {
                    const contactId = value ? parseInt(value, 10) : null;
                    setSelectedContactId(contactId);
                    setValue("contactId", contactId);
                    if (contactId) setMissingPrimaryContact(false);
                  }}
                  placeholder={getSelectedCompanyId() ? t("deals.form.select_contact", "Seleziona contatto") : independentContact ? t("deals.form.search_contact", "Cerca contatto...") : t("deals.form.select_company_first", "Seleziona prima l'azienda")}
                  searchPlaceholder={t("deals.form.search_contact", "Cerca contatto...")}
                  selectedLabel={contactOptions.find((option) => option.value === selectedContactId?.toString())?.label}
                  disabled={(!getSelectedCompanyId() && !independentContact) || isLoadingContacts}
                />
                {missingPrimaryContact && <p role="alert" className="text-sm font-medium text-destructive">Seleziona il contatto principale. Gli altri partecipanti non lo sostituiscono.</p>}
                {!missingPrimaryContact && requiresPrimaryContact && !selectedContactId && <p className="text-xs text-amber-800">Richiesto per questo Deal.</p>}
              </div>

              <div className="space-y-3 border-t border-border pt-4">
                <div>
                  <Label>Altri partecipanti al Deal</Label>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Aggiungi più Contatti, Aziende o Filiali. Sono partecipanti ordinari del Deal, non Sinergie.
                  </p>
                </div>

                {relatedContactSuggestions.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-muted-foreground">Collegati al gruppo aziendale</p>
                    <div className="flex flex-wrap gap-1.5">
                      {relatedContactSuggestions.map((contact) => {
                        const selected = participantSelections.some((item) => item.entityType === "contact" && item.entityId === contact.id);
                        const alreadySynergy = draftSynergies.some((item) => item.entityType === 'contact' && item.entityId === contact.id);
                        return (
                          <Button
                            key={contact.id}
                            type="button"
                            size="sm"
                            variant={selected ? 'default' : 'outline'}
                            disabled={alreadySynergy}
                            className="h-auto min-h-8 max-w-full px-2 py-1 text-left text-xs"
                            onClick={() => {
                              setParticipantSelections((current) => selected
                                ? current.filter((item) => !(item.entityType === "contact" && item.entityId === contact.id))
                                : [...current, { entityType: "contact", entityId: contact.id, label: contact.label }]);
                            }}
                            title={alreadySynergy ? `${contact.label} è già selezionato come Sinergia` : `${contact.label} · ${contact.context}`}
                          >
                            <span className="truncate">{contact.label} · {contact.context}</span>
                          </Button>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-3 border border-border">
                  {(["contact", "company", "branch"] as DealParticipantType[]).map((type) => (
                    <Button key={type} type="button" variant={participantType === type ? "default" : "ghost"} className="rounded-none" onClick={() => setParticipantType(type)}>
                      {type === "contact" ? "Contatto" : type === "company" ? "Azienda" : "Filiale"}
                    </Button>
                  ))}
                </div>

                <CrmRecordAutocomplete
                  entityType={participantType}
                  value=""
                  onValueChange={() => {}}
                  onOptionSelect={(option) => {
                    const id = Number(option?.value);
                    if (!option || !Number.isSafeInteger(id) || id <= 0) return;
                    setParticipantSelections((current) => current.some((item) => item.entityType === participantType && item.entityId === id)
                      ? current
                      : [...current, { entityType: participantType, entityId: id, label: option.label }]);
                  }}
                  excludeIds={excludedOrdinaryParticipantIds}
                  placeholder={`Cerca e aggiungi ${participantType === "contact" ? "un contatto" : participantType === "company" ? "un'azienda" : "una filiale"}...`}
                  searchPlaceholder="Cerca in tutto il CRM..."
                />

                {participantSelections.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {participantSelections.map((participant) => (
                      <Badge key={`${participant.entityType}:${participant.entityId}`} variant="secondary" className="max-w-full gap-1 rounded-none">
                        {participant.entityType === "contact" ? <User className="h-3 w-3" /> : participant.entityType === "company" ? <Building2 className="h-3 w-3" /> : <GitBranch className="h-3 w-3" />}
                        <span className="truncate">{participant.label}</span>
                        <button
                          type="button"
                          onClick={() => {
                            const persisted = existingDealParticipants?.find((item) => item.entityType === participant.entityType && Number(item.entityId) === participant.entityId);
                            if (persisted) {
                              setParticipantDecision({ id: persisted.id, label: participant.label, entityType: participant.entityType, entityId: participant.entityId });
                            } else {
                              setParticipantSelections((current) => current.filter((item) => !(item.entityType === participant.entityType && item.entityId === participant.entityId)));
                            }
                          }}
                          aria-label={`Rimuovi ${participant.label}`}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
              {!isEditMode && (
                <div className="space-y-3 border-t border-border pt-4">
                  <div>
                    <Label>Sinergie dell'opportunità</Label>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Aggiungi intermediari, segnalatori o partner: saranno salvati come Sinergie quando creerai il Deal. Il contatto e l'azienda principali non vanno aggiunti qui.
                    </p>
                  </div>
                  <div className="grid grid-cols-3 border border-border">
                    {(['contact', 'company', 'branch'] as DealParticipantType[]).map((type) => (
                      <Button key={type} type="button" variant={synergyType === type ? 'default' : 'ghost'} className="rounded-none" onClick={() => setSynergyType(type)}>
                        {type === 'contact' ? 'Contatto' : type === 'company' ? 'Azienda' : 'Filiale'}
                      </Button>
                    ))}
                  </div>
                  <CrmRecordAutocomplete
                    entityType={synergyType}
                    value=""
                    onValueChange={() => {}}
                    onOptionSelect={(option) => {
                      const id = Number(option?.value);
                      if (!option || !Number.isSafeInteger(id) || id <= 0) return;
                      setDraftSynergies((current) => current.some((item) => item.entityType === synergyType && item.entityId === id)
                        ? current : [...current, { entityType: synergyType, entityId: id, label: option.label, role: '' }]);
                    }}
                    excludeIds={excludedSynergyIds}
                    placeholder="Cerca e aggiungi una Sinergia..."
                    searchPlaceholder="Cerca in tutto il CRM..."
                  />
                  {draftSynergies.map((synergy) => (
                    <div key={`${synergy.entityType}:${synergy.entityId}`} className="flex flex-wrap items-center gap-2 border border-border p-2">
                      <span className="min-w-0 flex-1 truncate text-sm">{synergy.label}</span>
                      <Input
                        className="h-8 w-40 text-xs"
                        aria-label={`Ruolo di ${synergy.label}`}
                        placeholder="Ruolo (facoltativo)"
                        maxLength={100}
                        value={synergy.role}
                        onChange={(event) => setDraftSynergies((current) => current.map((item) => item === synergy ? { ...item, role: event.target.value } : item))}
                      />
                      <Button type="button" size="sm" variant="ghost" aria-label={`Rimuovi Sinergia ${synergy.label}`} onClick={() => setDraftSynergies((current) => current.filter((item) => item !== synergy))}>
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Notes */}
            <div className="space-y-2 border-t pt-4">
              <Label htmlFor="notes">{t("deals.form.notes", "Note")}</Label>
              <VoiceAssistantTextarea
                id="notes" 
                placeholder={t("deals.form.notes_placeholder", "Aggiungi note sull'opportunità qui")}
                value={watch("notes") || ""}
                onValueChange={(value) => setValue("notes", value, { shouldDirty: true })}
                fieldLabel="Note Opportunità"
                martinaContext={{ terminalType: "entity", entityType: "deal", entityId: initialData?.id ? String(initialData.id) : undefined, route: initialData?.id ? `/deals/${initialData.id}` : "/deals" }}
              />
            </div>
            
            {/* Actions */}
            <SheetFooter>
              <Button type="button" variant="outline" onClick={() => requestClose(false)}>
                {t("common.cancel", "Annulla")}
              </Button>
              <Button type="submit" disabled={saveDeal.isPending}>
                {saveDeal.isPending ? t("common.saving", "Salvataggio in corso...") : isEditMode ? t("deals.actions.update_deal", "Aggiorna opportunità") : t("deals.actions.create_deal", "Crea opportunità")}
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>

      <AssociationDecisionDialog
        open={participantDecision !== null}
        onOpenChange={(isOpen) => { if (!isOpen) setParticipantDecision(null); }}
        target="deal_participant"
        associationId={participantDecision?.id ?? null}
        label={participantDecision ? `${participantDecision.label} · partecipante dell'opportunità` : ''}
        onApplied={() => {
          if (!participantDecision) return;
          setParticipantSelections((current) => current.filter((item) => !(item.entityType === participantDecision.entityType && item.entityId === participantDecision.entityId)));
          queryClient.invalidateQueries({ queryKey: [`/api/deals/${initialData?.id}/participants`] });
          invalidateDealViews(queryClient, initialData?.id, ['participants']);
        }}
      />

      {reviewContactQueue.length > 0 && (
        <RecordDrawer
          open
          elevated
          title="Verifica nuovo contatto"
          onClose={() => setReviewContactQueue((current) => current.slice(1))}
        >
          <ContactDrawerContent id={reviewContactQueue[0]} onOpenFullProfile={(id) => { if (requestClose(false)) { setReviewContactQueue([]); navigate(`/contacts/${id}`); } }} />
        </RecordDrawer>
      )}
      {reviewContactQueue.length === 0 && reviewDealId != null && (
        <RecordDrawer
          open
          elevated
          title="Opportunità"
          onClose={() => setReviewDealId(null)}
        >
          <DealDrawerContent id={reviewDealId} />
        </RecordDrawer>
      )}
    </>
  );
}
